import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import { RootState, AppDispatch } from "../app/store";
import { GiftBox } from "../utils/boxTypes";
import { User } from "keycloakauthmodule";

// Define the slice state interface
interface ReceivedGiftBoxesState {
  recievedGiftBoxes: GiftBox[];
  isLoading: boolean;
  currentPage: number;
  boxesPerPage: number;
  totalItems: number;
  selectedGiftBox: GiftBox | null;
  redeemGiftStatus: "idle" | "loading" | "succeeded" | "failed";
  redeemGiftError: string | null;
  isRedeemGiftInProgress: boolean;
}

const initialState: ReceivedGiftBoxesState = {
  recievedGiftBoxes: [],
  isLoading: false,
  currentPage: 1,
  boxesPerPage: 20,
  totalItems: 0,
  selectedGiftBox: null,
  redeemGiftStatus: "idle",
  redeemGiftError: null,
  isRedeemGiftInProgress: false,
};

// Async thunk for redeeming a gift via coupon
export const redeemGiftViaCoupon = createAsyncThunk<
  void,
  string, // Coupon code as argument
  {
    dispatch: AppDispatch;
    state: RootState;
    rejectValue: string;
  }
>(
  "receivedGiftBoxes/redeemGiftViaCoupon",
  async (couponCode, { rejectWithValue }) => {
    try {
      await axios.post(`/gifts/redeem-via-coupon?coupon_code=${couponCode}`);
    } catch (error: any) {
      console.error("Error redeeming gift via coupon:", error);
      // Extract error message from response
      const errorMessage =
        error.response?.data?.detail || "Failed to redeem gift via coupon.";

      return rejectWithValue(errorMessage);
    }
  }
);

// Async thunk for fetching received gift boxes
export const fetchReceivedGiftBoxes = createAsyncThunk<
  { list: GiftBox[]; total: number }, // Return type
  void, // Argument type
  {
    dispatch: AppDispatch;
    state: RootState;
    rejectValue: string;
  }
>(
  "receivedGiftBoxes/fetchReceivedGiftBoxes",
  async (_, { getState, rejectWithValue }) => {
    const state = getState();
    const { currentPage, boxesPerPage } = state.receivedGiftBoxes;

    const offset = (currentPage - 1) * boxesPerPage;

    try {
      const response = await axios.get(`/gifts/received`, {
        params: { offset, limit: boxesPerPage },
      });

      const { list, total, users } = response.data;

      // Map users into GiftBox.user_details
      const updatedList = list.map((box: GiftBox) => {
        const user = users.find((u: User) => u.id === box.receiver_id) || users.find((u: User) => u.id === box.sender_id);
        return {
          ...box,
          user_details: user ?? box.user_details, // fallback if not found
        };
      });

      return {
        list: updatedList, // return the updated list
        total,
      };

    } catch (error: any) {
      console.error("Error fetching received gift boxes:", error);
      return rejectWithValue("Failed to fetch received gift boxes");
    }
  }
);

// Create the slice
const receivedGiftBoxesSlice = createSlice({
  name: "receivedGiftBoxes",
  initialState,
  reducers: {
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setBoxesPerPage: (state, action: PayloadAction<number>) => {
      state.boxesPerPage = action.payload;
    },
    setSelectedGiftBox: (state, action: PayloadAction<GiftBox | null>) => {
      state.selectedGiftBox = action.payload;
    },
    resetRedeemGiftStatus: (state) => {
      state.redeemGiftStatus = "idle";
      state.redeemGiftError = null;
      state.isRedeemGiftInProgress = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchReceivedGiftBoxes.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchReceivedGiftBoxes.fulfilled, (state, action) => {
        const { list, total } = action.payload;
        state.recievedGiftBoxes = list;
        // If selectedGiftBox exists, update it with the new one from the fetched list
        if (state.selectedGiftBox) {
          const updatedGiftBox = list.find(
            (box) => box.id === state.selectedGiftBox?.id
          );
          if (updatedGiftBox) {
            state.selectedGiftBox = updatedGiftBox;
          }
        }
        state.totalItems = total;
        state.isLoading = false;
      })
      .addCase(fetchReceivedGiftBoxes.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(redeemGiftViaCoupon.pending, (state) => {
        state.redeemGiftStatus = "loading";
        state.isRedeemGiftInProgress = true;
      })
      .addCase(redeemGiftViaCoupon.fulfilled, (state) => {
        state.redeemGiftStatus = "succeeded";
        state.isRedeemGiftInProgress = false;
        state.redeemGiftError = null;
      })
      .addCase(redeemGiftViaCoupon.rejected, (state, action) => {
        state.redeemGiftStatus = "failed";
        state.isRedeemGiftInProgress = false;
        state.redeemGiftError = action.payload || "An error occurred.";
      });
  },
});

// Export actions and reducer
export const {
  setCurrentPage,
  setBoxesPerPage,
  setSelectedGiftBox,
  resetRedeemGiftStatus,
} = receivedGiftBoxesSlice.actions;
export default receivedGiftBoxesSlice.reducer;
